import React from 'react';
import { Helmet } from "react-helmet";
import { Route, Switch } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { request } from '../../../functions/apiRequestHelper';
import { gtmPageview, gtmPush } from '../../../functions/gtm';
import smoothScroll from '../../../functions/smoothScroll';
import { unFormatNumber, formatNumberComma } from '../../../functions/textFormatting';
import NavBar from '../../../audi-ui-components/NavBar';
import StepVehicle from './StepVehicle';
import StepLoan from './StepLoan';
import StepSummary from './StepSummary';
import StepRepayments from './StepRepayments';
import queryString from 'query-string';
import {
    APPLICATION_CONTAINER_ID,
    UI_AUDI,
    PATH_AFS_CALCULATOR,
    DEFAULT_THEME
} from '../../../constants';
import './calculator.scss';

const STEP_VEHICLE = "vehicle";
const STEP_LOANTYPE = "loan-type";
const STEP_SUMMARY = "summary";
const STEP_REPAYMENTS = "repayments";

const balloonDefault = {
    "12": 60,
    "24": 50,
    "36": 40,
    "48": 30,
    "60": 20
};

const defaultState = {
    calculating: false,
    counter: 0,
    responseSummary: {},
    responseParams: {},
    responseCopy: {},
    responseError: false,
    variant: null,
    engine: null,
    locState: "",
    prices: null,
    loantype: "ConsumerLoan",
    term: null,
    frequency: "weekly",
    deposit: 0,
    balloon: null,
    kmlimit: 15000,
    gfv: "AudiChoice",
    gfvAllowed: true
};

class AFSCalculator extends React.Component {

    constructor(props) {
        super(props);
        this.state = defaultState;
    }

    componentDidMount() {
        const {match, history, location} = this.props;
        if (match.params.step) {
            // redirect to start of form
            history.push(`${PATH_AFS_CALCULATOR}${location.search}`);
        }
        gtmPageview(PATH_AFS_CALCULATOR);
    }

    getNavIdxFromSlug = (slug) => {
        if (slug === STEP_VEHICLE) { return 0; }
        if (slug === STEP_LOANTYPE) { return 1; }
        if (slug === STEP_SUMMARY) { return 2; }
        if (slug === STEP_REPAYMENTS) {return 2; }
        return null;
    }

    onNavBarClick = (idx) => {
        const {history, location} = this.props;
        const {variant} = this.state;
        if (idx === 0 || (variant && variant.value)) {
            let paths = ["", STEP_LOANTYPE, STEP_SUMMARY];
            history.push(`${PATH_AFS_CALCULATOR}/${paths[idx]}${location.search}`);
        }
    }

    onSubmitVehicle = (data) => {
        const {history, location} = this.props;
        if (!this.state.engine || data.engine.value !== this.state.engine.value) {
            let d = (unFormatNumber(data.prices.VehicleDriveAwayPrice) / 100) * 10;
            d = d.toFixed(2);
            this.setState({
                variant: data.variant,
                engine: data.engine,
                locState: data.locState,
                prices: data.prices,
                deposit: d,
                gfvAllowed: true
            }, this.calculate);
        }
        history.push(`${PATH_AFS_CALCULATOR}/${STEP_LOANTYPE}${location.search}`);
        smoothScroll.scrollTo(APPLICATION_CONTAINER_ID);
    }

    onSubmitLoanType = (data) => {
        const {history, location} = this.props;
        if (data.loantype !== this.state.loantype) {
            this.calculate(data);
        }
        history.push(`${PATH_AFS_CALCULATOR}/${STEP_SUMMARY}${location.search}`);
        smoothScroll.scrollTo(APPLICATION_CONTAINER_ID);
    }

    onSubmitRepayments = (data, navigate, cb) => {
        this.calculate(data, cb);
        if (navigate) {
            const {history, location} = this.props;
            history.push(`${PATH_AFS_CALCULATOR}/${STEP_SUMMARY}${location.search}`);
            smoothScroll.scrollTo('afs-calculator-nav');
        }
    }

    calculate = (data, cb) => {
        const {variant, engine, prices, counter} = this.state;
        if (typeof data === "undefined") { data = {}; }
        if (!data.loantype) { data.loantype = this.state.loantype; }
        if (!data.gfv) { data.gfv = this.state.gfv; }
        if (typeof data.deposit === "undefined") { data.deposit = this.state.deposit; }
        if (!data.kmlimit) { data.kmlimit = this.state.kmlimit; }
        if (!data.term) { data.term = this.state.term; }
        if (!data.balloon && data.balloon !== 0) { data.balloon = this.state.balloon; }
        let termChanged = data.term !== this.state.term;
        let balloonChanged = data.balloon !== this.state.balloon && data.balloon !== null;
        let payloadParams = [
            {
                "id": "FinanceProduct",
                "text": data.loantype,
                "changed": data.loantype !== this.state.loantype
            },
            {
                "id": "DownPayment",
                "text": data.deposit,
                "changed": data.deposit !== this.state.deposit
            },
            {
                "id": "KMLimit",
                "text": data.kmlimit,
                "changed": data.kmlimit !== this.state.kmlimit
            }
        ];
        if (data.term && data.term !== null) {
            payloadParams.push({
                "id": "Term",
                "text": data.term,
                "changed": termChanged
            });
        }
        if (data.balloon && data.balloon !== null) {
            payloadParams.push({
                "id": "Balloon",
                "text": data.balloon,
                "changed": balloonChanged
            });
        }
        let payload = {
            "product": {
                "parameter": payloadParams,
                "id": data.gfv === "AudiChoice" ? "AudiChoice" : data.loantype,
                "text": data.loantype
            },
            "vehicle": {
                "key": engine.modelCode,
                "year": engine.year,
                "priceTotal": unFormatNumber(prices.VehicleDriveAwayPrice),
                "priceModel": unFormatNumber(prices.VehicleDriveAwayPrice)
            },
            "domain": "AUDI.CC.AUS",
            "name": "Defaults"
        };
        this.setState({calculating: true, ...data});
        request(`${process.env.RAZZLE_API_HOST}/1/afs/calculator`, {
            method: "POST",
            body: JSON.stringify(payload)
        }).then((response) => {
            // console.log(response);
            let summary = {};
            let params = {};
            let copy = {};
            if (response.summary && response.parameters) {
                if (response.summary.detailGroups && response.summary.detailGroups.detailGroup && response.summary.detailGroups.detailGroup.detail) {
                    let len = response.summary.detailGroups.detailGroup.detail.length;
                    for (let i=0; i<len; i++) {
                        let d = response.summary.detailGroups.detailGroup.detail[i];
                        summary[d.id] = d;
                    }
                }
                if (response.parameters.group && response.parameters.group.parameter) {
                    let len = response.parameters.group.parameter.length;
                    for (let i=0; i<len; i++) {
                        let p = response.parameters.group.parameter[i];
                        params[p.id] = p;
                    }
                }
                copy.description = response.parameters.description;
                copy.productDisclaimer = response.parameters.productDisclaimer;
                copy.product = response.summary.product;
            }
            let newTerm = termChanged ? data.term : params["Term"] && params["Term"].default ? parseInt(params["Term"].default) : 48;
            let newBalloon = balloonChanged ? data.balloon : params["Balloon"] ? balloonDefault[newTerm.toString()] : null;
            if (typeof newBalloon === "undefined") { newBalloon = 0; }
            // validate balloon
            if (newBalloon && params.Balloon && params.Balloon.dataValues) {
                let len = params.Balloon.dataValues.length;
                let bMin = parseInt(params.Balloon.dataValues[0]);
                let bMax = parseInt(params.Balloon.dataValues[len - 1]);
                if (newBalloon < bMin) {
                    newBalloon = bMin;
                } else if (newBalloon > bMax) {
                    newBalloon = bMax;
                }
            }
            let newGfvAllowed = true;
            if (data.gfv === "AudiChoice" && typeof params["GFV"] === "undefined") {
                // gfv loan requested, standard loan returned. need to set balloon value as the api sets it to 0
                newGfvAllowed = false;
                newBalloon = balloonDefault[newTerm.toString()];
                summary["Balloon"].value = "" + newBalloon;
                let amt = ((newBalloon / 100) * payload.vehicle.priceTotal).toFixed(2);
                summary["BalloonAmount"].value = formatNumberComma(amt);
            }
            this.setState({
                counter: counter + 1,
                term: newTerm,
                balloon: newBalloon,
                calculating: false,
                responseError: false,
                responseSummary: summary,
                responseParams: params,
                responseCopy: copy,
                gfvAllowed: newGfvAllowed,
                gfv: newGfvAllowed ? data.gfv : "custom"
            }, () => {
                if (typeof cb === "function") {
                    cb();
                }
            });
        }).catch((error) => {
            console.error(error);
            this.setState({calculating: false, responseError: true});
        });
    }

    reset = () => {
        this.setState(defaultState);
        this.props.history.push(`${PATH_AFS_CALCULATOR}/${this.props.location.search}`);
        smoothScroll.scrollTo('afs-calculator-nav');
    }

    render() {
        const {match, location} = this.props;
        const {responseCopy} = this.state;
        const step = match.params.step || STEP_VEHICLE;
        const query = queryString.parse(location.search);
        const theme = query.theme || DEFAULT_THEME;
        return (
            <Layout ui={query.ui || UI_AUDI} embedded={query.embedded} wide={query.wide} theme={theme}>
                <Helmet>
                    <title>Finance Calculator | Audi Financial Services</title>
                </Helmet>
              
                <div className="audi-forms__page pb-0 afs-calculator">
                    
                    {step === STEP_VEHICLE && <div className="row py-5">
                        <div className="col-12 col-medium-8">
                            <h1 className="aui-headline-2 mb-3">Create your tailored finance solution</h1>
                            <p>We are here to help you create an individually tailored finance solution that’s perfect for your budget, your lifestyle and your new vehicle. Simply run the numbers with no commitments or send your enquiry to a dealer who’ll be able to help with finalising your application.</p>
                        </div>
                    </div>}
                    {step === STEP_LOANTYPE && <div className="row py-5">
                        <div className="col-12 col-medium-8">
                            <h1 className="aui-headline-2 mb-3">Choose what's right for you</h1>
                            <p>No matter whether you’re buying your Audi for business or pleasure, we have an excellent range of flexible options for you to choose from. By helping you select the right option, we can even help save you money as well as time.</p>
                        </div>
                    </div>}
                    {step === STEP_SUMMARY && <div className="row py-5">
                        <div className="col-12 col-medium-8">
                            <h1 className="aui-headline-2 mb-3">Calculate your loan repayments</h1>
                            <p>Tailor your loan to suit your lifestyle, with the option of reducing your monthly repayments by paying a deposit upfront and adding a final payment (balloon) at the end of your term.</p>
                        </div>
                    </div>}
                    {step === STEP_REPAYMENTS && <div className="row py-5">
                        <div className="col-12 col-medium-8">
                            <h1 className="aui-headline-2 mb-3">Tailor your repayments</h1>
                            <p>Tailor your loan to suit your lifestyle, with the option of reducing your monthly repayments by paying a deposit upfront and adding a final payment (balloon) at the end of your term.</p>
                        </div>
                    </div>}
                </div>

                <div className="aui-color-black py-3 afs-calculator" id="afs-calculator-nav">
                    <div className="audi-forms__page pb-0">
                        <NavBar
                            actions={[
                                { label: '01. Confirm a vehicle' },
                                { label: '02. Choose a loan' },
                                { label: '03. Calculate repayments' }
                            ]}
                            activeIndex={this.getNavIdxFromSlug(step)}
                            onActionClick={this.onNavBarClick}
                            theme="black"
                            small
                        />
                    </div>
                </div>
                <div className="audi-forms__page afs-calculator">
                    <div className="py-5">
                      {step === STEP_VEHICLE &&
                        <StepVehicle
                            variantId={this.state.variant ? this.state.variant.value : ""}
                            engineId={this.state.engine ? this.state.engine.value : ""}
                            locState={this.state.locState}
                            onSubmit={this.onSubmitVehicle}
                            theme={theme}
                        />
                      }
                      {(step === STEP_LOANTYPE && this.state.variant) &&
                        <StepLoan
                            loantype={this.state.loantype}
                            onClickBack={() => { this.props.history.push(`${PATH_AFS_CALCULATOR}/${this.props.location.search}`); }}
                            onSubmit={this.onSubmitLoanType}
                            theme={theme}
                        />
                      }
                      {(step === STEP_SUMMARY && this.state.variant) &&
                        <StepSummary
                            variant={this.state.variant}
                            prices={this.state.prices}
                            locState={this.state.locState}
                            onClickBack={() => { this.props.history.push(`${PATH_AFS_CALCULATOR}/${STEP_LOANTYPE}${this.props.location.search}`); }}
                            onClickEdit={() => {
                                this.props.history.push(`${PATH_AFS_CALCULATOR}/${STEP_REPAYMENTS}${this.props.location.search}`);
                                smoothScroll.scrollTo('afs-calculator-nav');
                            }}
                            onClickReset={this.reset}
                            calculating={this.state.calculating}
                            responseSummary={this.state.responseSummary}
                            responseError={this.state.responseError}
                            loantype={this.state.loantype}
                            gfv={this.state.gfv}
                            engine={this.state.engine}
                            disclaimer={responseCopy.productDisclaimer}
                            description={responseCopy.description}
                            theme={theme}
                        />
                      }
                      {(step === STEP_REPAYMENTS && this.state.variant) && 
                        <StepRepayments
                            onSubmit={this.onSubmitRepayments}
                            onClickBack={() => {
                                this.props.history.push(`${PATH_AFS_CALCULATOR}/${STEP_LOANTYPE}${this.props.location.search}`);
                                smoothScroll.scrollTo('afs-calculator-nav');
                            }}
                            onClickCancel={() => {
                                this.props.history.push(`${PATH_AFS_CALCULATOR}/${STEP_SUMMARY}${this.props.location.search}`);
                                smoothScroll.scrollTo('afs-calculator-nav');
                            }}
                            loantype={this.state.loantype}
                            term={this.state.term}
                            frequency={this.state.frequency}
                            deposit={this.state.deposit}
                            balloon={this.state.balloon}
                            kmlimit={this.state.kmlimit}
                            gfv={this.state.gfv}
                            gfvAllowed={this.state.gfvAllowed}
                            params={this.state.responseParams}
                            calculating={this.state.calculating}
                            counter={this.state.counter}
                            theme={theme}
                        />
                      }

                      <hr className="my-5 aui-color-gray90" />

                      {(step === STEP_SUMMARY || step === STEP_REPAYMENTS) && <>
                        {responseCopy.description && <div className="aui-small aui-color-text-gray90 mb-3" dangerouslySetInnerHTML={{__html: responseCopy.description}} />}
                        {responseCopy.productDisclaimer && <div className="aui-small aui-color-text-gray90 mb-3" dangerouslySetInnerHTML={{__html: responseCopy.productDisclaimer}} />}
                      </>}
                    
                      {step === STEP_REPAYMENTS && <>
                        <p className="aui-small aui-color-text-gray90 mb-3">** For approved customers who meet the Audi Financial Services credit and serviceability assessment and eligibility criteria. Terms and conditions apply.</p>
                        <p className="aui-small aui-color-text-gray90 mb-3">*** A deposit and lump sum payments are optional and can assist with reducing the amount of your regular repayments for your vehicle. A final lump sum payment or balloon has higher interest charges if a guaranteed future value (GFV)^ or balloon is selected.</p>
                      </>}

                    </div>
                </div>
            </Layout>
        );
    }
}

export default AFSCalculator;
